import React from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import LoyaltyTierCoinStack from '~/components/Icons/LoyaltyTierCoinStack';
import InfiniteLogo from '~/components/LuxLoyalty/InfiniteLogo';
import SocieteProgramNameLogo from '~/components/LuxLoyalty/SocieteProgramNameLogo';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import CampaignsPage from './Campaigns/CampaignsPage';
import InfiniteConciergePage from './InfiniteConcierge/InfiniteConciergePage';
import LoyaltyCustomRulePage from './LoyaltyCustomRulePage';
import LoyaltyManualAccrualsPage from './LoyaltyManualAccrualsPage';
import StatsPage from './Stats/StatsPage';

const LOYALTY_ADMIN_ROLES = [ROLE_LUX_LOYALTY_ADMIN];
// Custom Rules is accessible to general admins as well as loyalty admins.
const CUSTOM_RULE_ROLES = [ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN];

// Tab to land on by default when no (or an inaccessible) section is provided.
const DEFAULT_TAB_SLUG = 'campaigns';

interface TabConfig {
  label: string;
  slug: string;
  component: React.ComponentType;
  requiredRoles?: Array<string>;
}

export const TabsMap: Record<string, TabConfig> = {
  campaigns: {
    label: 'Campaigns',
    slug: 'campaigns',
    component: CampaignsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  stats: {
    label: 'Offers',
    slug: 'stats',
    component: StatsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  infinite: {
    label: 'Infinite',
    slug: 'infinite',
    component: InfiniteConciergePage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'manual-accruals': {
    label: 'Manual Accruals',
    slug: 'manual-accruals',
    component: LoyaltyManualAccrualsPage,
  },
  'custom-rule': {
    label: 'Custom Rules',
    slug: 'custom-rule',
    component: LoyaltyCustomRulePage,
    requiredRoles: CUSTOM_RULE_ROLES,
  },
} as const;

type TabKey = keyof typeof TabsMap;

type TabBannerVariant = 'platinum' | 'infinite';

interface TabBannerConfig {
  variant: TabBannerVariant;
  description: string;
}

const TAB_BANNER_CONFIG: Record<TabKey, TabBannerConfig> = {
  campaigns: {
    variant: 'platinum',
    description: 'Set up and manage member promotions, bonus points, and campaign eligibility.',
  },
  stats: {
    variant: 'platinum',
    description:
      'Generate complimentary eSIMs, manage Pay for Points offer settings, and view Société rewards inventory reservations.',
  },
  infinite: {
    variant: 'infinite',
    description: 'Enrol customers into Infinite and manage their dedicated concierge representatives.',
  },
  'manual-accruals': {
    variant: 'platinum',
    description: 'Manually add or adjust points when a booking did not earn automatically.',
  },
  'custom-rule': {
    variant: 'platinum',
    description: 'Create one-off earn rules for specific orders or customer situations.',
  },
};

const INFINITE_LOGO_HEIGHT = 20;
const INFINITE_LOGO_WIDTH = (248 / 38) * INFINITE_LOGO_HEIGHT;
const SOCIETE_LOGO_HEIGHT = 20;
const SOCIETE_LOGO_WIDTH = (62 / 14) * SOCIETE_LOGO_HEIGHT;
const TAB_BANNER_COIN_SIZE = 24;

function LoyaltyTabBanner({ tab }: { tab: TabKey }) {
  const { variant, description } = TAB_BANNER_CONFIG[tab];
  const tier = variant === 'infinite' ? 'infinite' : 'platinum';

  return (
    <Box
      sx={(theme) => ({
        mt: 2,
        mb: 2,
        px: { xs: 2.5, md: 3 },
        py: 2,
        borderRadius: 2,
        color: theme.palette.loyaltyTiers[tier].contrastText,
        background: theme.palette.loyaltyTiers[tier].gradient,
        boxShadow: theme.shadows[2],
      })}
    >
      <Stack direction="row" alignItems="center" spacing={2.5} flexWrap="nowrap">
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, color: 'inherit' }}>
          {variant !== 'infinite' && <LoyaltyTierCoinStack size={TAB_BANNER_COIN_SIZE} />}
          <Box sx={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}>
            {variant === 'infinite' ? (
              <InfiniteLogo width={INFINITE_LOGO_WIDTH} height={INFINITE_LOGO_HEIGHT} aria-hidden />
            ) : (
              <SocieteProgramNameLogo width={SOCIETE_LOGO_WIDTH} height={SOCIETE_LOGO_HEIGHT} aria-hidden />
            )}
          </Box>
        </Stack>
        <Typography
          variant="body1"
          sx={{
            color: 'inherit',
            lineHeight: 1.5,
            fontWeight: 400,
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Box>
  );
}

preRegisterSpotlightRoutes(expandTabRoutes('/societe', 'Societe', TabsMap));

export default function LoyaltyPageContainer() {
  const { section: rawSection } = useParams<{ section?: TabKey }>();
  const { user } = useCurrentUser();

  const programName = LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName;

  const visibleTabs = Object.keys(TabsMap).filter((tab) => {
    const { requiredRoles } = TabsMap[tab];
    if (!requiredRoles) return true;
    return canShowTab(user, requiredRoles);
  });

  const currentTab = TabsMap[rawSection];
  const isTabVisible = visibleTabs.includes(rawSection);
  const CurrentComponent = isTabVisible ? currentTab?.component : undefined;

  // When no (or an inaccessible) section is requested, redirect to the default
  // tab if the user can see it, otherwise to the first tab available to them.
  // This keeps the landing tab role-aware so users never hit an empty page.
  if (!isTabVisible && visibleTabs.length > 0) {
    const fallbackTab = visibleTabs.includes(DEFAULT_TAB_SLUG) ? DEFAULT_TAB_SLUG : visibleTabs[0];
    return <Navigate to={`/societe/${fallbackTab}`} replace />;
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{programName}</title>
      </Helmet>

      {isTabVisible && rawSection && <LoyaltyTabBanner tab={rawSection} />}

      <Box
        sx={(theme) => ({
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1.5,
          '& .MuiTab-root': {
            minHeight: 48,
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.secondary',
            transition: 'color 0.2s ease',
            '&:hover': { color: 'text.primary' },
          },
          '& .MuiTab-root.Mui-selected': {
            color: theme.palette.primary.main,
          },
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        })}
      >
        <Tabs value={isTabVisible ? rawSection : false} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={TabsMap[tab].label}
              to={`/societe/${tab}`}
              component={Link}
              sx={{ minWidth: 30, px: 1.5 }}
            />
          ))}
        </Tabs>
      </Box>

      <Box>{CurrentComponent && <CurrentComponent />}</Box>
    </Container>
  );
}
